/** Text written outside Curated Planet — a client's AI, a guest — shown as received.
 *
 *  The backend marks such text `content_is_untrusted_data` (client MCP plan §5.3) and keeps it in
 *  one block, apart from our own words. The console renders it the same way everywhere:
 *
 *  - **quoted plain text**: a React text child in a `<blockquote>` (line breaks kept), never
 *    markup, never Markdown, never auto-linked — a `<script>`, an `<img>` or `[a](javascript:…)`
 *    reads as the characters it is;
 *  - **labelled**: the block names who wrote it and that it is data, never instructions, so
 *    nobody reads a client's "please refund" as ours;
 *  - **kept apart** from our fields (titles, findings, statuses), which stay outside the block.
 *
 *  `UntrustedBlock` is the labelled frame (one per payload, with the backend's legend);
 *  `UntrustedText` one quoted value inside it.
 *
 *  Tables and grids whose rows mix our fields with their words (a tenant's vocabulary, the
 *  knowledge gaps clients' AI left) use the inline forms: `UntrustedLegend` once above the
 *  table, `UntrustedInline` for each of their values in a cell — the same plain-text rule,
 *  monospace on a tinted ground, while our columns stay ordinary text. */
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

export function UntrustedBlock({
  title,
  legend,
  children,
}: {
  /** Who wrote it, e.g. "The client's words". */
  title: string;
  /** The payload's own legend (what it is and that it is data, not instructions). */
  legend: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="section"
      aria-label={`${title} (untrusted text)`}
      data-untrusted-block
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 2,
        bgcolor: 'background.default',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
        <ShieldOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} aria-hidden />
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="caption" color="text.secondary">
          · untrusted text
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 1.5 }}>
        {legend}
      </Typography>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
}

/** One quoted value: plain text, line breaks kept, long words broken. Empty text says so. */
export function UntrustedText({
  text,
  label,
  empty = 'Nothing written.',
}: {
  text: string | null | undefined;
  /** What this value is ("Summary", "Follow-up 2 · 25 Oct 09:41"). */
  label?: ReactNode;
  empty?: string;
}) {
  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
          {label}
        </Typography>
      )}
      {text ? (
        <Box
          component="blockquote"
          data-untrusted-text
          sx={{
            m: 0,
            pl: 1.5,
            py: 0.5,
            borderLeft: 3,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontFamily: 'monospace',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {text}
        </Box>
      ) : (
        <Typography variant="body2" color="text.disabled">
          {empty}
        </Typography>
      )}
    </Box>
  );
}

/** The legend of a table's or a grid's untrusted values: who wrote them, and that they are data,
 *  never instructions. One per table, above it; the cells use `UntrustedInline`. */
export function UntrustedLegend({ title, legend }: { title: string; legend: string }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="flex-start"
      role="note"
      aria-label={`${title} (untrusted text)`}
      data-untrusted-legend
      sx={{ mb: 1 }}
    >
      <ShieldOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} aria-hidden />
      <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Box>{' '}
        · untrusted text. {legend}
      </Typography>
    </Stack>
  );
}

/** One untrusted value inline — a table cell, a grid row, a sentence of ours that names it:
 *  plain text (a React text child), monospace on a tinted ground so it never reads as our
 *  words. `clamp` cuts a long value to that many lines (the whole text stays in the DOM). */
export function UntrustedInline({
  text,
  empty = '—',
  clamp,
}: {
  text: string | null | undefined;
  empty?: string;
  clamp?: number;
}) {
  if (!text) {
    return (
      <Typography variant="body2" component="span" color="text.disabled">
        {empty}
      </Typography>
    );
  }
  return (
    <Box
      component="span"
      data-untrusted-text
      sx={{
        fontFamily: 'monospace',
        fontSize: 13,
        lineHeight: 1.5,
        bgcolor: 'action.hover',
        borderRadius: 0.5,
        px: 0.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        ...(clamp
          ? {
              display: '-webkit-box',
              WebkitLineClamp: clamp,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }
          : {}),
      }}
    >
      {text}
    </Box>
  );
}
